<template>
  <div class="resonant-circuit-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$f₀ = 1 / [2π√(LC)]$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
             <label>当前回路电流 I_rms</label>
             <span class="val" :style="{color: resonanceInfo.color}">{{ currentAmp.toFixed(2) }} <small>A</small></span>
          </div>
          <div class="hud-item orange">
             <label>谐振偏离度 Δf</label>
             <span class="val">{{ Math.abs(freq - resFreq).toFixed(1) }} <small>Hz</small></span>
          </div>
        </div>

        <svg width="100%" height="260" viewBox="0 0 400 260" preserveAspectRatio="xMidYMid meet">
          <defs>
             <filter id="glow-res"><feGaussianBlur stdDeviation="6" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
          </defs>

          <!-- 电流波形示意 (Oscilloscope) -->
          <path :d="oscPath" fill="none" :stroke="resonanceInfo.color" :stroke-width="isResonant ? 3 : 1.5" 
                :filter="isResonant ? 'url(#glow-res)' : ''" opacity="0.6" />

          <!-- 电路原理图 (简图) -->
          <g transform="translate(100, 180)" stroke="#1e293b" stroke-width="2">
             <rect x="-30" y="-15" width="60" height="30" fill="#020617" stroke="#64748b" /> <!-- L -->
             <text x="0" y="5" text-anchor="middle" fill="#64748b" font-size="10" stroke="none">L</text>
             <line x1="60" y1="0" x2="90" y2="0" stroke="#334155" />
             <line x1="90" y1="-15" x2="90" y2="15" stroke="#94a3b8" stroke-width="3" />
             <line x1="100" y1="-15" x2="100" y2="15" stroke="#94a3b8" stroke-width="3" />
             <text x="130" y="5" text-anchor="middle" fill="#94a3b8" font-size="10" stroke="none">C</text>
          </g>

          <!-- 频率响应曲线图 (Right Side) -->
          <g transform="translate(260, 40)">
             <path :d="curvePath" fill="none" stroke="#334155" stroke-width="1.5" />
             <!-- 谐振点标尺 -->
             <line :x1="resX" y1="0" :x2="resX" y2="120" stroke="#94a3b8" stroke-dasharray="2,2" opacity="0.3" />
             <circle :cx="currentX" :cy="currentY" r="4" :fill="resonanceInfo.color" filter="url(#glow-res)" />
             <text x="70" y="135" text-anchor="middle" fill="#64748b" font-size="8">幅频特性 I(f)</text>
          </g>
        </svg>

        <div class="resonance-state" :style="{color: resonanceInfo.color}">
           {{ resonanceInfo.text }}
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex flex-row items-center gap-6">
              <div class="flex-1">
                 <label class="s-label">驱动信号频率 (f_drive)</label>
                 <el-slider v-model="freq" :min="10" :max="120" size="small"></el-slider>
              </div>
              <div class="flex-1">
                 <label class="s-label">谐振频率调节 (f₀ - 调谐电容)</label>
                 <el-slider v-model="resFreq" :min="30" :max="90" size="small"></el-slider>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-odometer"></i>
           <p><strong>核心揭示：</strong>LCR电路具有“选频”特性。当外界信号频率与电路固有谐振频率 $ f_0 $ 对齐时，电感与电容的电抗相互抵消，阻抗降至最低，电流 $ I $ 达到峰值。这就是收音机选台和无线电滤波的根本物理原理：<strong>只有“步调一致”的能量才能通过。</strong></p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'ResonantCircuitViz',
  data() {
    return {
      freq: 100, // Driving freq
      resFreq: 60, // Self resonant freq tune
      t: 0
    }
  },
  computed: {
    // Lorentzian function for amplitude response
    currentAmp() {
       const q = 40; // Simulated Quality factor
       const dx = (this.freq - this.resFreq) * 0.1;
       return 10 / (1 + q * dx * dx);
    },
    isResonant() { return Math.abs(this.freq - this.resFreq) < 5; },
    resonanceInfo() {
       if (this.currentAmp > 7) return { text: '完美谐振 (Resonance Peak)', color: '#10b981' };
       if (this.currentAmp > 2) return { text: '邻域选频 (Tuned Region)', color: '#fbbf24' };
       return { text: '失谐状态 (Detuned)', color: '#64748b' };
    },
    // Response curve path calculation
    curvePath() {
       let points = [];
       for (let f = 10; f <= 120; f += 2) {
          const dx = (f - this.resFreq) * 0.1;
          const y = 120 - (10 / (1 + 40 * dx * dx)) * 10;
          points.push(`${(f - 10) * 1.2},${y}`);
       }
       return "M" + points.join("L");
    },
    currentX() { return (this.freq - 10) * 1.2; },
    currentY() { return 120 - this.currentAmp * 10; },
    resX() { return (this.resFreq - 10) * 1.2; },
    oscPath() {
       let points = [];
       const amp = this.currentAmp * 8;
       for (let x = 0; x < 250; x += 3) {
          const y = 100 + Math.sin(x * 0.2 + this.t) * amp;
          points.push(`${x},${y}`);
       }
       return "M" + points.join("L");
    }
  },
  methods: {
    animate() {
       this.t += 0.2 * (this.freq / 60);
       this.animId = requestAnimationFrame(this.animate);
    }
  },
  mounted() { this.animate(); },
  beforeDestroy() { cancelAnimationFrame(this.animId); }
}
</script>

<style scoped lang="scss">
.resonant-circuit-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #eff6ff; color: #1e40af; border: 1px solid #bfdbfe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 260px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px; &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #f97316; } label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; } } }
.resonance-state { position: absolute; bottom: 15px; width: 100%; text-align: center; font-size: 12px; font-weight: 900; text-transform: uppercase; letter-spacing: 1px; }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; }
.s-label { font-size: 11px; color: #64748b; font-weight: 800; margin-bottom: 2px; display: block; }
.flex { display: flex; } .items-center { align-items: center; } .gap-6 { gap: 24px; } .flex-1 { flex: 1; }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: center; gap: 10px; i { color: #2563eb; font-size: 1.2rem; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
</style>
